import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Chip, LinearProgress, Stack, Card, CardContent,
  Table, TableBody, TableCell, TableHead, TableRow, Tabs, Tab, IconButton,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  Alert, CircularProgress, Tooltip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DownloadIcon from '@mui/icons-material/Download';
import Layout from '../components/Layout';
import outboundCampaignService from '../services/outboundCampaignService';
import { io } from 'socket.io-client';
import authService from '../services/authService';

const STATUS_LABEL = {
  draft: '草稿', running: '進行中', completed: '已完成',
  cancelled: '已取消', paused: '已暫停', scheduled: '已排程'
};
const STATUS_COLOR = {
  draft: 'default', running: 'primary', completed: 'success',
  cancelled: 'default', paused: 'warning', scheduled: 'info'
};
const CONTACT_STATUS_LABEL = {
  pending: '待撥', calling: '撥打中', completed: '完成', failed: '失敗', skipped: '略過'
};
const CONTACT_STATUS_COLOR = {
  pending: 'default', calling: 'info', completed: 'success', failed: 'error', skipped: 'warning'
};

const OutboundCampaignDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [confirmStart, setConfirmStart] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    const [cRes, kRes] = await Promise.all([
      outboundCampaignService.get(id),
      outboundCampaignService.listContacts(id, { limit: 500 })
    ]);
    if (!cRes.success) {
      setError(cRes.message || '取得活動失敗');
      setLoading(false);
      return;
    }
    setCampaign(cRes.data);
    setContacts(kRes.data || []);
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Socket: 收到 call_log 即時刷新此活動（per-page io()，mirror RestaurantReservationsPage）
  useEffect(() => {
    const companyId = authService.getCompanyId();
    if (!companyId) return undefined;
    const socketUrl = process.env.REACT_APP_SOCKET_URL
      || process.env.REACT_APP_API_URL?.replace('/api', '')
      || window.location.origin;
    const token = localStorage.getItem('token') || localStorage.getItem('authToken');
    const handler = () => load();
    let socket;
    try {
      socket = io(socketUrl, {
        auth: { token },
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 2000,
      });
      const joinRoom = () => socket.emit('join_company', { companyId, userId: localStorage.getItem('userId') || 'anonymous' });
      socket.on('connect', joinRoom);
      socket.on('reconnect', joinRoom);
      socket.on('call_log.created', handler);
      socket.on('call_log.updated', handler);
    } catch (e) { /* socket 失敗不擋頁面 */ }
    return () => { if (socket) socket.disconnect(); };
  }, [load]);

  const handleStart = async () => {
    setStarting(true);
    const res = await outboundCampaignService.start(id);
    setStarting(false);
    setConfirmStart(false);
    if (!res.success) {
      setError(res.message || '開始撥打失敗');
      return;
    }
    setError('');
    load();
  };

  const handleExport = async () => {
    try {
      // axios with token via apiService — direct fetch using token from localStorage
      const token = localStorage.getItem('authToken');
      const apiUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
      const url = `${apiUrl}/api/outbound-campaigns/${id}/export`;
      const resp = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `campaign-${id}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      setError(`匯出失敗：${e.message}`);
    }
  };

  if (loading) {
    return <Layout><Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box></Layout>;
  }
  if (!campaign) {
    return <Layout><Alert severity="error" sx={{ m: 3 }}>{error || '活動不存在'}</Alert></Layout>;
  }

  const total = campaign.totalContacts || 0;
  const done = campaign.completedContacts || 0;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const filtered = filter === 'all' ? contacts : contacts.filter(c => c.status === filter);

  return (
    <Layout>
    <Box sx={{ p: 3, maxWidth: 1200 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton onClick={() => navigate('/outbound-campaigns')}><ArrowBackIcon /></IconButton>
        <Typography variant="h5" sx={{ flex: 1 }}>{campaign.name}</Typography>
        <Chip
          label={STATUS_LABEL[campaign.status] || campaign.status}
          color={STATUS_COLOR[campaign.status] || 'default'}
        />
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            流程：{campaign.voiceFlow?.name || '（未設定）'}
            {campaign.description && ` · ${campaign.description}`}
          </Typography>
          <Box sx={{ mb: 0.5, display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2">進度：{done} / {total}</Typography>
            <Typography variant="body2">{pct}%</Typography>
          </Box>
          <LinearProgress variant="determinate" value={pct} sx={{ height: 10, borderRadius: 1, mb: 2 }} />

          <Stack direction="row" spacing={3} sx={{ mb: 2 }}>
            <Stat label="總數" value={total} />
            <Stat label="已完成" value={done} />
            <Stat label="成功" value={campaign.successfulContacts || 0} color="success.main" />
            <Stat label="失敗" value={campaign.failedContacts || 0} color="error.main" />
          </Stack>

          <Stack direction="row" spacing={1}>
            {campaign.status === 'draft' && (
              <Button
                variant="contained"
                startIcon={<PlayArrowIcon />}
                onClick={() => setConfirmStart(true)}
                disabled={!campaign.flowId || total === 0}
              >
                開始撥打
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
              disabled={total === 0}
            >
              匯出 CSV
            </Button>
          </Stack>
          {campaign.status === 'draft' && total === 0 && (
            <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block' }}>
              此活動尚無聯絡人，請重新建立活動並上傳 Excel。
            </Typography>
          )}
        </CardContent>
      </Card>

      <Tabs value={filter} onChange={(_, v) => setFilter(v)} sx={{ mb: 1 }}>
        <Tab value="all" label={`全部 (${contacts.length})`} />
        <Tab value="pending" label="待撥" />
        <Tab value="calling" label="撥打中" />
        <Tab value="completed" label="完成" />
        <Tab value="failed" label="失敗" />
      </Tabs>

      <Card variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>姓名</TableCell>
              <TableCell>電話</TableCell>
              <TableCell>狀態</TableCell>
              <TableCell>嘗試次數</TableCell>
              <TableCell>最後撥打時間</TableCell>
              <TableCell>通話紀錄</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>沒有符合條件的聯絡人</TableCell></TableRow>
            ) : filtered.map(c => (
              <TableRow key={c.id} hover>
                <TableCell>{c.name || '—'}</TableCell>
                <TableCell>{c.phone}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={CONTACT_STATUS_LABEL[c.status] || c.status}
                    color={CONTACT_STATUS_COLOR[c.status] || 'default'}
                  />
                </TableCell>
                <TableCell>{c.attemptCount || 0}</TableCell>
                <TableCell>{c.lastAttemptedAt ? new Date(c.lastAttemptedAt).toLocaleString() : '—'}</TableCell>
                <TableCell>
                  {c.callLogId ? (
                    <Tooltip title="開啟通話紀錄">
                      <Button size="small" onClick={() => navigate(`/call-logs?id=${c.callLogId}`)}>
                        查看
                      </Button>
                    </Tooltip>
                  ) : '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={confirmStart} onClose={() => setConfirmStart(false)}>
        <DialogTitle>確認開始撥打</DialogTitle>
        <DialogContent>
          <DialogContentText>
            將立即開始撥打 <strong>{total}</strong> 通電話，間隔 10 秒。撥打開始後無法暫停（Phase 1 限制）。確定要開始嗎？
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmStart(false)} disabled={starting}>取消</Button>
          <Button onClick={handleStart} variant="contained" disabled={starting}>
            {starting ? '啟動中…' : '確定開始'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </Layout>
  );
};

const Stat = ({ label, value, color }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="h6" sx={{ color: color || 'text.primary' }}>{value}</Typography>
  </Box>
);

export default OutboundCampaignDetailPage;
